import type { ReactNode } from "react";
import type { createAgentClient } from "../api/agentClient";
import type { BoardQueueRow } from "../api/clinicalWorkspace";
import { boardStatusLabels, isScheduled } from "../lib/todayBoard";
import { TodayPatientSnapshot } from "./TodayPatientSnapshot";

export function TodayPatientDetails({
  id,
  expanded,
  siteId,
  row,
  client,
  onReview,
  roomAction,
  intakeAction,
  closeAction,
  onComplete,
  disabled,
}: {
  readonly id: string;
  readonly expanded: boolean;
  readonly siteId: string;
  readonly row: BoardQueueRow;
  readonly client: Partial<Pick<ReturnType<typeof createAgentClient>, "getPatientWorkspace">>;
  readonly onReview: () => void;
  readonly roomAction: ReactNode;
  /** Send intake to phone / Show code (PA-5), when the visit can get a questionnaire code. */
  readonly intakeAction?: ReactNode;
  /** Cancel visit, when the recorded stage allows closing from Today. */
  readonly closeAction: ReactNode;
  readonly onComplete: () => void;
  readonly disabled: boolean;
}) {
  return (
    <section
      id={id}
      className="tb-inline-details"
      aria-label={`Patient details for ${row.displayName}`}
      hidden={!expanded}
    >
      {expanded ? (
        <div className="tb-details-grid">
          <div className="tb-detail-header">
            <section className="tb-detail-reason">
              <h3 className="tb-eyebrow">Reason for visit</h3>
              <p className="tb-chief">{row.reason.trim() || "Not recorded"}</p>
            </section>
            <section className="tb-detail-stage" aria-label="Visit so far">
              <span className="tb-current-stage">
                <span aria-hidden="true" />
                {isScheduled(row) ? "Awaiting check-in" : boardStatusLabels[row.status]}
              </span>
              {!isScheduled(row) ? (
                <span className="tb-muted">{row.waitMinutes} minutes since arrival</span>
              ) : null}
            </section>
            <div className="tb-detail-actions">
              <button type="button" className="tb-button" onClick={onReview}>
                Review patient chart
              </button>
              {roomAction}
              {intakeAction}
              {row.appointmentStatus === "ready_for_discharge" ? (
                <button
                  type="button"
                  className="tb-button"
                  disabled={disabled}
                  onClick={onComplete}
                  title="A signed encounter note is required; signing marks the visit done automatically"
                >
                  Mark done
                </button>
              ) : null}
              {closeAction}
            </div>
          </div>
          <TodayPatientSnapshot siteId={siteId} row={row} client={client} />
        </div>
      ) : null}
    </section>
  );
}
